import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Loader } from "semantic-ui-react";

import { useCreateProduct, usePostUSalesUnit, useUploadProductImage } from "../../../api/product";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON, UNIT_TYPES } from "../../../config/constants";
import { IsRootProduct } from "../../../config/enums/ProductType.enum";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import ProductInfoForm from "./component/ProductInfoForm";
import ProductUnitForm from "./component/ProductUnitForm";
import { loadTableData } from "./component/ProductUnitTable";
import { ProductFormData, ProductRegisterModalProps, ProductUnit } from "./types/productTypes";

import "./productRegister.scss";

const ProductRegisterModal: React.FC<ProductRegisterModalProps> = ({
  visibleModal,
  setVisibleModal
}) => {
  const { isLoading, mutate, reset: resetData } = useCreateProduct();
  const { isLoading: isLoadingProductImage, mutate: uploadProductImage } = useUploadProductImage();
  const { isLoading: isLoadingSalesUnit, mutate: uploadSalesUnit } = usePostUSalesUnit();

  const [addProductUnit, setProductUnit] = useState<ProductUnit[]>([]);
  const [productType, setProductType] = useState<string>("PROCESS");
  const [defaultType, setDefaultType] = useState<string>(UNIT_TYPES[0].value);
  const [imageData, setImageData] = useState<File | null>(null);
  const [isRootProduct, setIsRootProduct] = useState<string>(IsRootProduct.NO);
  const [enableSizeEditStatus, setEnableSizeEditStatus] = useState<boolean>(false);
  const [currentUpdateIndex, setCurrentUpdateIndex] = useState<any>(null);

  const isRootProductYes = isRootProduct === IsRootProduct.YES;
  const isRootProductBool = useMemo(
    () => (!DISABLE_IS_ROOT_PRODUCT_BUTTON ? isRootProduct === IsRootProduct.YES : false),
    [isRootProduct]
  );
  const enableUnitSizeAdd = useMemo(() => {
    return (
      (isRootProduct === IsRootProduct.YES && addProductUnit.length === 0) ||
      isRootProduct === IsRootProduct.NO
    );
  }, [isRootProduct, addProductUnit]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue,
    trigger
  } = useForm<ProductFormData>({
    mode: "all"
  });

  const {
    register: registerUnitSize,
    formState: { errors: errorsUnit },
    handleSubmit: handleSubmitUnitSize,
    reset: resetUnitSize,
    setValue: setUnitValue,
    control
  } = useForm<ProductUnit>();

  useEffect(() => {
    setIsRootProduct(IsRootProduct.NO);
    return () => {
      setProductUnit([]);
      setDefaultType(UNIT_TYPES[0].value);
      reset();
      resetUnitSize();
      resetData();
      setEnableSizeEditStatus(false);
    };
  }, [visibleModal]);

  useEffect(() => {
    if (isRootProductBool && addProductUnit.length == 1) {
      setValue("enableEditNavigation", true);
      handleSubmit(onSubmit)();
    }
  }, [addProductUnit, isRootProductBool]);

  const closeModalStatus = () => {
    const value = getValues();
    setVisibleModal(true, value);
  };

  const saveProductImage = (data: any) => {
    if (imageData) {
      const productImageData = {
        product_id: data?._id,
        file: imageData
      };
      uploadProductImage(productImageData, {
        onSuccess: () => {
          finishProductImageSave();
        }
      });
    } else {
      successMessage("Product created successfully");
      closeModalStatus();
    }
  };

  const saveUnitData = (productData: any) => {
    if (addProductUnit.length > 0) {
      const productUnitData = {
        product_id: productData._id,
        sales_units: addProductUnit.map(unit => ({
          ...unit,
          sales_unit_name: unit.sales_unit_name.trim()
        }))
      };
      uploadSalesUnit(productUnitData, {
        onSuccess: () => {
          saveProductImage(productData);
        }
      });
    }
  };

  const setUnitSizeUpdate = (data: ProductUnit, index: any) => {
    setEnableSizeEditStatus(true);
    setCurrentUpdateIndex(index);
    setDefaultType(data.sales_unit_type);
    setUnitValue("sales_unit_name", data.sales_unit_name);
    setUnitValue("sales_unit_size", data.sales_unit_size);
    setUnitValue("sales_unit_type", data.sales_unit_type);
    setUnitValue("ean_barcode", data.ean_barcode);
  };

  const finishProductImageSave = () => {
    successMessage("Product created successfully");
    closeModalStatus();
  };

  const removeDataHandler = (index: number) => {
    const newData = [...addProductUnit];
    newData.splice(index, 1);
    setProductUnit(newData);
  };

  const checkProductSaveCondition = (data: ProductFormData): boolean => {
    if (isEmpty(data.product_name.trim())) {
      errorView("Product name is required");
      return true;
    } else if (addProductUnit.length <= 0) {
      errorView("Please add product unit sizes", { force: true });
      return true;
    }
    return false;
  };

  const addUnitData = async (data: ProductUnit) => {
    const currentAddSalesUnitData = {
      ...data,
      sales_unit_name: data.sales_unit_name.trim(),
      sales_unit_type: defaultType
    };
    const isValidText = await trigger();
    if ((isRootProductBool && isValidText) || !isRootProductBool) {
      setProductUnit([...addProductUnit, currentAddSalesUnitData]);
      return resetUnitSize();
    }
  };

  const cancel = () => {
    closeModalStatus();
    reset();
  };

  const saveProduct = () => {
    const values = getValues();
    setValue("enableNavigate", false);
    onSubmit(values);
  };

  const enableAverageProduct = (data: any) => {
    setIsRootProduct(data);
  };

  const onSubmit = async (productDetails: ProductFormData) => {
    if (!checkProductSaveCondition(productDetails)) {
      const productData = {
        ...productDetails,
        product_name: productDetails.product_name.trim(),
        product_other_info: productDetails.product_other_info?.trim() ?? "",
        product_type: productType,
        sales_units: [],
        product_ingredients: [],
        is_average_product: isRootProductBool
      };
      mutate(productData, {
        onSuccess: data => {
          setValue("productData", { ...data, product_id: data._id });
          saveUnitData(data);
        }
      });
    }
  };

  const editUnitSize = (data: any) => {
    setProductUnit(prev =>
      prev.map((user, i) =>
        i === currentUpdateIndex ? { ...data, sales_unit_type: defaultType } : user
      )
    );
    setCurrentUpdateIndex(null);
    setEnableSizeEditStatus(false);
    setDefaultType(UNIT_TYPES[0].value);
    resetUnitSize();
  };

  if (isLoading || isLoadingSalesUnit || isLoadingProductImage) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          closeModalStatus();
          reset();
        }}
        centered={true}
        title={"Add New Product"}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <CommonSplitContainer
            topChild={
              <>
                <ProductInfoForm
                  register={register}
                  errors={errors}
                  productType={productType}
                  setProductType={setProductType}
                  isRootProduct={isRootProduct}
                  enableAverageProduct={enableAverageProduct}
                  setImageData={setImageData}
                />
                <ProductUnitForm
                  registerUnitSize={registerUnitSize}
                  errorsUnit={errorsUnit}
                  defaultType={defaultType}
                  setDefaultType={setDefaultType}
                  handleSubmitUnitSize={handleSubmitUnitSize}
                  addUnitData={addUnitData}
                  addProductUnit={addProductUnit}
                  enableUnitSizeAdd={enableUnitSizeAdd}
                  loadTableData={() =>
                    loadTableData(
                      addProductUnit,
                      removeDataHandler,
                      setUnitSizeUpdate,
                      setDefaultType
                    )
                  }
                  enableSizeEditStatus={enableSizeEditStatus}
                  editUnitSize={editUnitSize}
                  control={control}
                />
              </>
            }
            bottomChild={
              <MainBottomMultipleButtonView
                leftButtons={[{ title: "Cancel", onClick: cancel, buttonOutLine: true }]}
                rightButtons={[
                  {
                    contentView: 16,
                    title: isRootProductYes ? "Save and Continue" : "Save",
                    onClick: () => handleSubmit(saveProduct)(),
                    buttonView: true,
                    disabled:
                      (errors && Object.keys(errors).length > 0) ||
                      (errorsUnit && Object.keys(errorsUnit).length > 0)
                        ? true
                        : false
                  }
                ]}
              />
            }
          />
        </form>
      </CommonModal>
    </>
  );
};

export default ProductRegisterModal;
